import React from "react";
import { RefreshCw, Clock, Pencil, Eye, EyeOff } from "lucide-react";
import { ServiceTitanIntegrationState } from "@/modules/hub/features/CompanyManagement/hooks/useServiceTitanIntegration";

interface ServiceTitanCredentialsProps {
  heading?: React.ReactNode;
  state: ServiceTitanIntegrationState;
}

const ServiceTitanCredentials: React.FC<ServiceTitanCredentialsProps> = ({
  heading,
  state,
}) => {
  const {
    credential,
    editedClientId,
    editedClientSecret,
    editedTenantId,
    handleCancelEdit,
    handleRefreshBusinessUnits,
    handleSaveCredentials,
    isEditingCredentials,
    isRefreshing,
    isSavingCredentials,
    lastSyncTime,
    setEditedClientId,
    setEditedClientSecret,
    setEditedTenantId,
    setIsEditingCredentials,
    setShowClientSecret,
    showClientSecret,
  } = state;

  return (
    <div>
      {heading && <div className="mb-4">{heading}</div>}

      {isEditingCredentials ? (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              Tenant ID
            </label>
            <input
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100 focus:ring-indigo-500 focus:border-indigo-500"
              disabled={isSavingCredentials}
              onChange={(e) => setEditedTenantId(e.target.value)}
              placeholder="Enter tenant ID"
              type="text"
              value={editedTenantId}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              Client ID
            </label>
            <input
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100 focus:ring-indigo-500 focus:border-indigo-500"
              disabled={isSavingCredentials}
              onChange={(e) => setEditedClientId(e.target.value)}
              placeholder="Enter client ID"
              type="text"
              value={editedClientId}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              Client Secret
            </label>
            <div className="relative">
              <input
                className="w-full px-3 py-2 pr-10 text-sm border border-gray-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100 focus:ring-indigo-500 focus:border-indigo-500"
                disabled={isSavingCredentials}
                onChange={(e) => setEditedClientSecret(e.target.value)}
                placeholder="Enter client secret"
                type={showClientSecret ? "text" : "password"}
                value={editedClientSecret}
              />
              <button
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200"
                disabled={isSavingCredentials}
                onClick={() => setShowClientSecret(!showClientSecret)}
                type="button"
              >
                {showClientSecret ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={
                isSavingCredentials ||
                !editedTenantId ||
                !editedClientId ||
                !editedClientSecret
              }
              onClick={handleSaveCredentials}
            >
              {isSavingCredentials ? "Verifying & Saving..." : "Save & Verify"}
            </button>
            <button
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-slate-300 bg-gray-100 dark:bg-slate-700 rounded-md hover:bg-gray-200 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSavingCredentials}
              onClick={handleCancelEdit}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center text-sm text-gray-500 dark:text-slate-400">
              <Clock className="h-4 w-4 mr-1 flex-shrink-0" />
              <span>Last Sync: {lastSyncTime || "Never"}</span>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <button
                className="inline-flex w-full items-center justify-center whitespace-nowrap px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-md text-sm font-medium text-gray-700 dark:text-slate-300 bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors sm:w-auto"
                onClick={() => setIsEditingCredentials(true)}
                title="Edit credentials"
              >
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </button>

              <button
                className="inline-flex w-full items-center justify-center whitespace-nowrap px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors sm:w-auto"
                disabled={isRefreshing || !credential?.tenantId}
                onClick={handleRefreshBusinessUnits}
              >
                <RefreshCw
                  className={`h-4 w-4 mr-2 ${isRefreshing ? "animate-spin" : ""}`}
                />
                Refresh Business Units
              </button>
            </div>
          </div>

          <div className="overflow-auto ring-1 ring-gray-200 dark:ring-slate-700 md:rounded-lg">
            <table className="min-w-full divide-y divide-gray-300 dark:divide-slate-700">
              <thead className="bg-gray-50 dark:bg-slate-700/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-300 uppercase tracking-wider">
                    Tenant ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-300 uppercase tracking-wider">
                    Client ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-300 uppercase tracking-wider">
                    Client Secret
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-slate-800">
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-slate-100">
                    {credential?.tenantId || "Not configured"}
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-slate-100">
                    {credential?.clientId || "Not configured"}
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-slate-100">
                    {credential?.hasClientSecret
                      ? "••••••••"
                      : "Not configured"}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceTitanCredentials;
